import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ShieldCheck, UserPlus, X } from "lucide-react";

export default function SiteAdminsCard() {
  const [admins, setAdmins] = useState([]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/admin/site-admins").then((r) => setAdmins(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/site-admins", { email });
      toast.success(`${email} is now a site admin. They'll see Admin next time they sign in.`);
      setEmail(""); load();
    } catch (er) { toast.error(er?.response?.data?.detail || "Couldn't add admin."); }
    finally { setBusy(false); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Remove site admin access for ${a.name || a.email}?`)) return;
    try { await api.delete(`/admin/site-admins/${a.id}`); toast.success("Admin access removed."); load(); }
    catch (er) { toast.error(er?.response?.data?.detail || "Couldn't remove."); }
  };

  return (
    <section className="card-surface p-6 mb-8" data-testid="site-admins-card">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck size={16} style={{ color: "var(--gold)" }} />
        <h3 className="font-serif-display text-xl" style={{ color: "var(--ink)" }}>Site admins</h3>
      </div>
      <p className="text-xs mb-4" style={{ color: "var(--muted)" }}>Full access to every organization, the journal, books, newsletters and billing. They need an account first.</p>
      <ul className="mb-4 divide-y" style={{ borderColor: "var(--border-subtle)" }} data-testid="site-admins-list">
        {admins.map((a) => (
          <li key={a.id} className="flex items-center justify-between gap-3 py-2" data-testid={`site-admin-${a.id}`}>
            <div className="min-w-0">
              <div className="text-sm truncate" style={{ color: "var(--ink)" }}>{a.name || a.email}{a.is_owner && <span className="ml-2 text-[0.55rem] uppercase tracking-[0.16em]" style={{ color: "var(--gold)" }}>Owner</span>}</div>
              <div className="text-xs truncate" style={{ color: "var(--muted)" }}>{a.email}</div>
            </div>
            {!a.is_owner && (
              <button onClick={() => remove(a)} className="p-1.5 shrink-0" style={{ color: "var(--terracotta)" }} title="Remove admin access" data-testid={`site-admin-remove-${a.id}`}><X size={14} /></button>
            )}
          </li>
        ))}
      </ul>
      <form onSubmit={add} className="flex flex-col sm:flex-row gap-2">
        <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="their account email" className="flex-1 px-3 py-2 text-sm" data-testid="site-admin-email-input" />
        <button type="submit" disabled={busy || !email} className="btn-primary text-xs" data-testid="site-admin-add-btn"><UserPlus size={13} /> {busy ? "Adding…" : "Make site admin"}</button>
      </form>
    </section>
  );
}
